import {
  Card,
  Text,
  InlineGrid,
  BlockStack,
  Box,
  DataTable,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { ChatIcon, ChartLineIcon, CashDollarIcon } from '@shopify/polaris-icons';
import { useAiUsageStats } from '../../hooks/useAiUsage';
import { StatCard } from '../shared/StatCard';

function formatCost(usd: number): string {
  if (usd < 0.001) return '<$0.001';
  return `$${usd.toFixed(4)}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

interface AiUsageStatsProps {
  days?: number;
}

export function AiUsageStats({ days = 30 }: AiUsageStatsProps) {
  const { data, isLoading, isError } = useAiUsageStats(days);
  const stats = data?.data;

  if (isLoading) {
    return (
      <Box padding="400">
        <Spinner size="small" />
      </Box>
    );
  }

  if (isError || !stats) {
    return (
      <Banner tone="critical">
        <p>Failed to load AI usage statistics.</p>
      </Banner>
    );
  }

  const featureRows = Object.entries(stats.calls_by_feature).map(([feature, info]) => [
    feature,
    formatNumber(info.calls),
    formatNumber(info.tokens),
    formatCost(info.cost_usd),
  ]);

  return (
    <BlockStack gap="400">
      <Text as="h2" variant="headingMd">
        AI Usage — Last {days} Days
      </Text>

      <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
        <StatCard
          label="Total AI Calls"
          value={formatNumber(stats.total_calls)}
          icon={ChatIcon}
          iconColor="indigo"
        />
        <StatCard
          label="Total Tokens"
          value={formatNumber(stats.total_tokens)}
          icon={ChartLineIcon}
          iconColor="teal"
        />
        <StatCard
          label="Total Cost"
          value={formatCost(stats.total_cost_usd)}
          icon={CashDollarIcon}
          iconColor="green"
          deltaLabel="USD"
        />
      </InlineGrid>

      {featureRows.length > 0 && (
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Usage by Feature</Text>
            <DataTable
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
              headings={['Feature', 'Calls', 'Tokens', 'Cost (USD)']}
              rows={featureRows}
            />
          </BlockStack>
        </Card>
      )}

      {featureRows.length === 0 && (
        <Card>
          <Box padding="400">
            <Text as="p" tone="subdued">No AI calls recorded in the last {days} days.</Text>
          </Box>
        </Card>
      )}
    </BlockStack>
  );
}
